import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { theme } from '../theme';
import { STATUS_LABELS, SECONDARY_AXIS_MAX_VISIBLE, POLLING_INTERVAL_MS } from '../constants';
import { smarttix, getActiveDistribution } from '../services/api';
import { TicketDetail, ActiveDistribution } from '../types';
import { CurrentUser } from '../auth/identity';
import { PageType } from './NavBar';
import { BentoCard, CategoryBadge, styleRedactionTokens, escapeHtmlText, PaperclipIcon, TrashIcon, LightningIcon, RevealContext, shouldShowAttachmentGlyph, mayShowAttachmentGlyph, AttachmentGlyphSlot } from './shared';
import { SecondaryAxisPills } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { formatAbsoluteDate } from '../utils/dateFormatting';
import { useTicketManager } from '../contexts/TicketManagerContext';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import { DistributionHero } from './DistributionHero';
import { useActivePagination } from '../hooks/useActivePagination';
import { useTicketDetailRefresh } from '../hooks/useTicketDetailRefresh';

// ─── Constants ───────────────────────────────────────────
type VerlaufBucket = 'uploads' | 'offen' | 'genehmigt';

const VERLAUF_TABS: { bucket: VerlaufBucket; label: string }[] = [
  { bucket: 'uploads', label: 'Uploads' },
  { bucket: 'offen', label: 'Offen' },
  { bucket: 'genehmigt', label: 'Genehmigt' },
];

// Module-level const — stable reference across renders, avoids hook re-fire.
const EMPTY_ACTIVE_FILTERS = {};

interface QuickUpload {
  id: string;
  fileName: string;
  uploading: boolean;
  processing?: boolean;
  deleting?: boolean;
  uuid?: string;
  subject?: string;
  attachmentCount?: number;
  error?: string;
}

// ─── Props ───────────────────────────────────────────────
interface DashboardSeiteProps {
  currentUser: CurrentUser | null;
  onNavigate: (page: PageType, params?: Record<string, string>) => void;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel elsewhere. ABSENT ⇒
  // true (see shouldShowAttachmentGlyph) — an omitted prop must reproduce the behaviour that
  // shipped before this rule existed, never a silent flip to the new posture.
  attachmentsCountInline?: boolean;
}

// Why: module-level so quick uploads survive page navigation (unmount/remount)
let persistedQuickUploads: QuickUpload[] = [];

// ═════════════════════════════════════════════════════════
// DashboardSeite
// ═════════════════════════════════════════════════════════
const DashboardSeite: React.FC<DashboardSeiteProps> = ({ currentUser, onNavigate, attachmentsCountInline }) => {
  const maintenance = useMaintenanceMode();
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const [activeTab, setActiveTab] = useState<VerlaufBucket>('uploads');
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [detailLoading, setSelectedTicketLoading] = useState(false);
  const [quickUploads, setQuickUploads] = useState<QuickUpload[]>(persistedQuickUploads);
  const [isDragOver, setIsDragOver] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hoveredUuid, setHoveredUuid] = useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [distribution, setDistribution] = useState<ActiveDistribution | null>(null);

  const isCompact = windowWidth < 900;

  // ─── Server-side pagination hooks ─────────────────────
  // Three Verlauf hooks — all sort by 'uploaded', no filters.
  // EMPTY_ACTIVE_FILTERS is module-level (stable ref) to prevent re-fetch on re-render.
  const uploadsHook = useActivePagination('uploads', 'uploaded', EMPTY_ACTIVE_FILTERS, 'desc');
  const offenHook   = useActivePagination('offen',   'uploaded', EMPTY_ACTIVE_FILTERS, 'desc');
  const genehmigtHook = useActivePagination('genehmigt', 'uploaded', EMPTY_ACTIVE_FILTERS, 'desc');

  // Dedicated pending hook — always mounted, mirrors offen bucket independently
  // (decoupled from Verlauf so Verlauf tab switches don't reset the pending card).
  const pendingHook = useActivePagination('offen', 'uploaded', EMPTY_ACTIVE_FILTERS, 'desc');

  const activeVerlaufHook = activeTab === 'uploads' ? uploadsHook
    : activeTab === 'offen' ? offenHook
    : genehmigtHook;
  const verlaufRows = activeVerlaufHook.rows;

  // All hooks carry the same server counts (same endpoint, same row).
  const counts = uploadsHook.counts;

  // Why: sync quickUploads to module-level so they survive page navigation
  useEffect(() => { persistedQuickUploads = quickUploads; }, [quickUploads]);

  // ─── Distribution refresh ─────────────────────────────
  const refreshDistribution = useCallback(() => {
    getActiveDistribution().then(d => { if (d) setDistribution(d); });
  }, []);

  // Stable refs so the poll closure captures current hook values without re-creating the interval.
  const uploadsHookRef = useRef(uploadsHook);
  const offenHookRef   = useRef(offenHook);
  const genehmigtHookRef = useRef(genehmigtHook);
  const pendingHookRef = useRef(pendingHook);
  uploadsHookRef.current   = uploadsHook;
  offenHookRef.current     = offenHook;
  genehmigtHookRef.current = genehmigtHook;
  pendingHookRef.current   = pendingHook;

  // Distribution poll: fetch once immediately, then every POLLING_INTERVAL_MS.
  // On each tick also silent-refresh Verlauf + pending lists via refresh() — no row
  // clear, no loading flash. Scroll position is irrelevant (refresh() never yanks rows).
  useEffect(() => {
    let cancelled = false;

    const tick = () => {
      getActiveDistribution().then(d => { if (!cancelled && d) setDistribution(d); });

      uploadsHookRef.current.refresh();
      offenHookRef.current.refresh();
      genehmigtHookRef.current.refresh();
      pendingHookRef.current.refresh();
    };

    tick();
    const id = setInterval(tick, POLLING_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Effects ──────────────────────────────────────────
  useEffect(() => {
    if (!selectedUuid) { setSelectedTicket(null); return; }
    let cancelled = false;
    setSelectedTicketLoading(true);
    smarttix.getTicket(selectedUuid).then(d => {
      if (!cancelled) { setSelectedTicket(d); setSelectedTicketLoading(false); }
    });
    return () => { cancelled = true; };
  }, [selectedUuid]);

  // ─── Transient-status detail refresh ────────────────
  useTicketDetailRefresh(selectedTicket, setSelectedTicket);

  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Clear selection when ticket leaves current Verlauf bucket
  useEffect(() => {
    if (!activeVerlaufHook.loading && selectedUuid) {
      if (!verlaufRows.find(t => t.uuid === selectedUuid)) {
        setSelectedUuid(null);
      }
    }
  }, [verlaufRows, selectedUuid, activeVerlaufHook.loading]);

  // ─── PII click-to-reveal ──────────────────────────────
  const [revealedPii, setRevealedPii] = useState<Set<string>>(() => new Set());
  const prevDetailUuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedTicket?.uuid !== prevDetailUuidRef.current) {
      prevDetailUuidRef.current = selectedTicket?.uuid ?? null;
      setRevealedPii(new Set());
    }
  }, [selectedTicket?.uuid]);

  const detailPersonalData = (selectedTicket?.personalData ?? null) as Record<string, any> | null;
  const detailCanReveal = !!selectedTicket && !!detailPersonalData &&
    (selectedTicket.status === 'submitted' || selectedTicket.status === 'ingested');
  const detailRevealCtx: RevealContext | undefined = detailCanReveal
    ? { personalData: detailPersonalData!, revealed: revealedPii }
    : undefined;

  const onDetailBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!detailCanReveal || !selectedTicket) return;
    let el = e.target as HTMLElement | null;
    while (el && el !== e.currentTarget && !el.dataset?.piiType) {
      el = el.parentElement;
    }
    if (!el || el === e.currentTarget) return;
    const type = el.dataset.piiType!;
    const idx = Number(el.dataset.piiIndex!);
    if (!Number.isFinite(idx)) return;
    const key = `${type}:${idx}`;
    setRevealedPii(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else { next.add(key); smarttix.logPiiReveal(selectedTicket.uuid, type, idx); }
      return next;
    });
  };

  // Auto-dismiss Schnell-Upload entries when their ticket progresses into the uploads bucket
  // (i.e. it appears in uploadsHook.rows) — once it transitions further (offen/genehmigt),
  // it leaves uploadsHook.rows and we dismiss it.
  useEffect(() => {
    if (uploadsHook.rows.length === 0 && !uploadsHook.loading) return;
    const uploadBucketUuids = new Set(uploadsHook.rows.map(t => t.uuid));
    setQuickUploads(prev => {
      // Keep entries that are still uploading (no uuid yet) OR are present in uploads bucket.
      // Entries whose uuid has left the uploads bucket (moved to offen/genehmigt) get dismissed.
      const next = prev.filter(u => !u.uuid || uploadBucketUuids.has(u.uuid));
      return next.length === prev.length ? prev : next;
    });
  }, [uploadsHook.rows, uploadsHook.loading]);

  // ─── Quick upload handlers ────────────────────────────
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files)
      .filter(f => f.name.endsWith('.eml') || f.name.endsWith('.msg'));
    for (let i = 0; i < droppedFiles.length; i++) {
      await ingestParseOnly(droppedFiles[i]);
      if (i < droppedFiles.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      await ingestParseOnly(file);
    }
  };

  const ingestParseOnly = async (file: File) => {
    const id = Date.now().toString();
    setQuickUploads(prev => [{ id, fileName: file.name, uploading: true }, ...prev]);
    try {
      const res = await smarttix.ingestTicket(file, { parseOnly: true });
      setQuickUploads(prev => prev.map(u => u.id === id
        ? { ...u, uploading: false, uuid: res.uuid, subject: res.subject, attachmentCount: res.attachmentCount }
        : u
      ));
    } catch (err) {
      console.error('[Schnell-Upload] failed:', file.name, err);
      setQuickUploads(prev => prev.map(u => u.id === id
        ? { ...u, uploading: false, error: 'Upload fehlgeschlagen' }
        : u
      ));
    }
  };

  const handleProcess = async (upload: QuickUpload) => {
    if (!upload.uuid || upload.processing) return;
    setQuickUploads(prev => prev.map(u => u.id === upload.id ? { ...u, processing: true } : u));
    const res = await tm.processTicket(upload.uuid);
    if (res.success) {
      setQuickUploads(prev => prev.filter(u => u.id !== upload.id));
    } else {
      setQuickUploads(prev => prev.map(u => u.id === upload.id ? { ...u, processing: false, error: res.error } : u));
    }
  };

  const handleDelete = async (upload: QuickUpload) => {
    if (!upload.uuid || upload.deleting) return;
    setQuickUploads(prev => prev.map(u => u.id === upload.id ? { ...u, deleting: true } : u));
    const res = await tm.deleteTicket(upload.uuid);
    if (res.success) {
      setQuickUploads(prev => prev.filter(u => u.id !== upload.id));
    } else {
      setQuickUploads(prev => prev.map(u => u.id === upload.id ? { ...u, deleting: false, error: res.error } : u));
    }
  };

  const handleHistoricalDelete = async (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tm.deletingUuids.has(uuid)) return;
    setQuickUploads(prev => prev.filter(u => u.uuid !== uuid));
    const res = await tm.deleteTicket(uuid);
    if (res?.success !== false) {
      activeVerlaufHook.removeRow(uuid);
      refreshDistribution();
    }
  };

  const handleHistoricalArchive = async (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tm.archivingUuids.has(uuid)) return;
    const res = await tm.archiveTicket(uuid);
    if (res?.success !== false) {
      activeVerlaufHook.removeRow(uuid);
      // Also remove from genehmigt in case cross-bucket state is cached
      genehmigtHook.removeRow(uuid);
      refreshDistribution();
    }
  };

  const handleRowMouseEnter = useCallback((uuid: string, el: HTMLElement) => {
    hoverTimerRef.current = setTimeout(() => {
      setHoveredUuid(uuid);
      setTooltipRect(el.getBoundingClientRect());
    }, 300);
  }, []);
  const handleRowMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredUuid(null);
    setTooltipRect(null);
  }, []);

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: theme.font.family }}>
      {/* Title */}
      <h1 style={{
        fontSize: 22, fontWeight: 600, color: theme.colors.navy,
        margin: 0, paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
      }}>
        Ihre Dashboard-Übersicht
      </h1>

      {/* 2-column layout */}
      <div style={{ display: 'flex', gap: theme.spacing.lg, flexDirection: isCompact ? 'column' : 'row' }}>

        {/* ════ LEFT COLUMN ═══════════════════════════════ */}
        <div style={{ flex: 1, minWidth: 0 }}>

          <DistributionHero distribution={distribution} />

          {/* ─── Verlauf ─────────────────────────────────── */}
          <BentoCard
            title="Verlauf"
            style={{ marginBottom: theme.spacing.lg }}
            contentStyle={{ minHeight: 220 }}
          >

            {/* Tabs (Style #1 — bottom border) */}
            <div style={{
              display: 'flex',
              borderBottom: `1px solid ${theme.colors.borderLight}`,
              marginBottom: theme.spacing.sm,
            }}>
              {VERLAUF_TABS.map(({ bucket, label }) => {
                const active = activeTab === bucket;
                const count = bucket === 'uploads' ? counts.uploads
                  : bucket === 'offen' ? counts.offen
                  : counts.genehmigt;
                return (
                  <button key={bucket} onClick={() => setActiveTab(bucket)} style={{
                    flex: 1, textAlign: 'center',
                    padding: '0 0 8px 0', background: 'none',
                    border: 'none', borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                    fontSize: 13, fontWeight: active ? 600 : 400,
                    color: active ? theme.colors.navy : theme.colors.textSecondary,
                    cursor: 'pointer', fontFamily: theme.font.family,
                  }}>
                    {label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Column headers */}
            <div style={{
              display: 'flex',
              padding: `${theme.spacing.xs}px 0`,
              paddingLeft: 3,
              fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
              textTransform: 'uppercase' as const, letterSpacing: 0.5,
            }}>
              <div style={{ flex: 1 }}>Betreff</div>
              <div style={{ width: 130, textAlign: 'right' }}>Aktionen</div>
            </div>

            {/* Rows (scrollable, infinite scroll via sentinel) */}
            <div className="stx-no-scrollbar" style={{ height: 175, overflowY: 'auto', margin: `0 -${theme.spacing.md}px` }}>
              {activeVerlaufHook.loading ? (
                <div style={{
                  padding: `${theme.spacing.lg}px ${theme.spacing.md}px`, textAlign: 'center',
                  fontSize: 13, color: theme.colors.textSecondary,
                }}>
                  Lade...
                </div>
              ) : verlaufRows.length === 0 ? (
                <div style={{
                  padding: `${theme.spacing.lg}px ${theme.spacing.md}px`, textAlign: 'center',
                  fontSize: 13, color: theme.colors.textSecondary,
                }}>
                  Kein Eintrag
                </div>
              ) : (
                verlaufRows.map(t => {
                  const sel = selectedUuid === t.uuid;
                  const tabStripColor = t.status === 'pipeline_error' ? theme.statusStrip.error
                    : t.primaryAxis ? cm.color(t.primaryAxis)
                    : theme.statusStrip.unclassified;
                  return (
                    <div
                      key={t.uuid}
                      onClick={() => setSelectedUuid(sel ? null : t.uuid)}
                      onMouseEnter={e => handleRowMouseEnter(t.uuid, e.currentTarget)}
                      onMouseLeave={handleRowMouseLeave}
                      style={{
                        display: 'flex',
                        backgroundColor: sel ? theme.colors.bgLight : 'transparent',
                        borderBottom: `1px solid ${theme.colors.borderLight}`,
                        cursor: 'pointer',
                        userSelect: sel ? 'text' : 'none',
                        opacity: (tm.deletingUuids.has(t.uuid) || tm.archivingUuids.has(t.uuid)) ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      {/* Compact category strip — honors rowLayout (abbr shown in strip_abbr mode).
                          No title= tooltip at all when there's no category yet (nothing to name);
                          the styled InfoTooltip only wraps the strip once t.primaryAxis is set. */}
                      {t.primaryAxis ? (
                        <InfoTooltip text={cm.label(t.primaryAxis)}>
                          <div style={{
                            width: cm.rowLayout === 'strip_abbr' ? 26 : 14,
                            flexShrink: 0,
                            backgroundColor: tabStripColor,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {cm.rowLayout === 'strip_abbr' && (
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.2, color: theme.colors.white }}>
                                {cm.abbr(t.primaryAxis)}
                              </span>
                            )}
                          </div>
                        </InfoTooltip>
                      ) : (
                        <div style={{
                          width: 14,
                          flexShrink: 0,
                          backgroundColor: tabStripColor,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }} />
                      )}
                      <div style={{
                        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
                        padding: `${theme.spacing.sm}px ${theme.spacing.md}px ${theme.spacing.sm}px 5px`,
                      }}>
                        <div style={{
                          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          <span style={{
                            fontSize: 13, fontWeight: 500, color: theme.colors.navy,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}
                            title={formatAbsoluteDate(t.receivedAt)}
                          >
                            {t.subject || '(Kein Betreff)'}
                          </span>
                          {/* Slot reserved only for rows that could get a glyph (mayShowAttachmentGlyph)
                              — reserving on a no-attachment row makes settle collapse the width and
                              its gap, the shift the slot exists to prevent. Slot size tracks the
                              PaperclipIcon size painted here (12), not TicketRowMeta's 13. */}
                          {mayShowAttachmentGlyph(t) && (
                            cm.configLoading ? <AttachmentGlyphSlot size={12} /> : shouldShowAttachmentGlyph(t, attachmentsCountInline ?? true) && (
                              <span title="Anhang" style={{ flexShrink: 0, display: 'flex' }}>
                                <PaperclipIcon size={12} />
                              </span>
                            )
                          )}
                        </div>
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {activeTab === 'uploads' && (
                            <>
                              <button
                                onClick={e => { e.stopPropagation(); onNavigate('uploads', { ticket: t.uuid }); }}
                                style={{
                                  padding: '2px 8px', fontSize: 11, fontWeight: 600, lineHeight: 1,
                                  fontFamily: theme.font.family,
                                  color: theme.colors.navy, backgroundColor: theme.colors.bgLight,
                                  border: `1px solid ${theme.colors.borderLight}`,
                                  borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                zur KI
                              </button>
                              <button
                                onClick={e => handleHistoricalDelete(t.uuid, e)}
                                disabled={tm.deletingUuids.has(t.uuid)}
                                title="Löschen"
                                style={{
                                  padding: '3px 4px', lineHeight: 1, display: 'flex', alignItems: 'center',
                                  background: 'none',
                                  border: `1px solid ${theme.colors.borderLight}`,
                                  borderRadius: theme.borderRadius.sm,
                                  cursor: tm.deletingUuids.has(t.uuid) ? 'default' : 'pointer',
                                  color: theme.colors.textSecondary,
                                  opacity: tm.deletingUuids.has(t.uuid) ? 0.3 : 0.7,
                                  transition: 'opacity 0.15s ease',
                                }}
                                onMouseEnter={e => { if (!tm.deletingUuids.has(t.uuid)) (e.currentTarget).style.opacity = '1'; }}
                                onMouseLeave={e => { (e.currentTarget).style.opacity = tm.deletingUuids.has(t.uuid) ? '0.3' : '0.7'; }}
                              >
                                <TrashIcon size={13} />
                              </button>
                            </>
                          )}
                          {activeTab === 'offen' && (
                            <button
                              onClick={e => { e.stopPropagation(); onNavigate('responses', { ticket: t.uuid }); }}
                              title="Antwortvorschlag prüfen"
                              style={{
                                padding: '2px 8px', fontSize: 11, fontWeight: 600, lineHeight: 1,
                                fontFamily: theme.font.family,
                                color: theme.colors.navy, backgroundColor: theme.colors.bgLight,
                                border: `1px solid ${theme.colors.borderLight}`,
                                borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              zum Vorschlag
                            </button>
                          )}
                          {activeTab === 'genehmigt' && (
                            <button
                              onClick={e => handleHistoricalArchive(t.uuid, e)}
                              disabled={tm.archivingUuids.has(t.uuid)}
                              style={{
                                padding: '2px 8px', fontSize: 11, fontWeight: 600, lineHeight: 1,
                                fontFamily: theme.font.family,
                                color: theme.colors.navy, backgroundColor: theme.colors.bgLight,
                                border: `1px solid ${theme.colors.borderLight}`,
                                borderRadius: theme.borderRadius.sm,
                                cursor: tm.archivingUuids.has(t.uuid) ? 'default' : 'pointer',
                                opacity: tm.archivingUuids.has(t.uuid) ? 0.5 : 1,
                                transition: 'opacity 0.15s ease',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Archivieren
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {/* Infinite scroll sentinel — only mounts when rows exist */}
              {!activeVerlaufHook.loading && verlaufRows.length > 0 && (
                <div ref={activeVerlaufHook.sentinelRef} aria-hidden="true" style={{ height: 1 }} />
              )}
              {activeVerlaufHook.loadingMore && (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: theme.colors.textSecondary }}>
                  Lade weitere…
                </div>
              )}
            </div>

            {/* Hover tooltip — rendered outside scroll container to avoid clipping */}
            {hoveredUuid && tooltipRect && (() => {
              const t = verlaufRows.find(x => x.uuid === hoveredUuid);
              if (!t) return null;
              const tooltipH = 120;
              const fitsBelow = tooltipRect.bottom + 4 + tooltipH <= window.innerHeight - 8;
              const top = fitsBelow ? tooltipRect.bottom + 4 : tooltipRect.top - tooltipH - 4;
              return (
                <div
                  style={{
                    position: 'fixed',
                    top,
                    right: Math.max(8, window.innerWidth - tooltipRect.right),
                    zIndex: 1000,
                    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                    backgroundColor: theme.colors.white,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.cardHover,
                    border: `1px solid ${theme.colors.borderLight}`,
                    fontSize: 12, fontFamily: theme.font.family,
                    whiteSpace: 'nowrap', pointerEvents: 'none',
                    animation: 'stx-slideIn 0.2s ease',
                  }}
                >
                  {[
                    ['Von', t.uploadedByName || t.uploadedByEmail || '—'],
                    ['Datum', formatAbsoluteDate(t.receivedAt)],
                    ['Status', STATUS_LABELS[t.status] ?? t.status],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', gap: 8, lineHeight: 1.6 }}>
                      <span style={{ color: theme.colors.textSecondary, minWidth: 60 }}>{label}</span>
                      <span style={{ color: theme.colors.navy }}>{value}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, lineHeight: 1.6, alignItems: 'center' }}>
                    <span style={{ color: theme.colors.textSecondary, minWidth: 60 }}>{cm.categoryNoun}</span>
                    {t.primaryAxis
                      ? <><CategoryBadge category={t.primaryAxis} style={{ fontSize: 9, padding: '1px 5px' }} />{t.isForceOverride && <span style={{ fontSize: 8, fontWeight: 600, color: theme.colors.deltaNegative, marginLeft: 2 }}>erzw.</span>}</>
                      : <span style={{ color: theme.colors.navy }}>{'—'}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 4, lineHeight: 1.6, alignItems: 'center' }}>
                    <span style={{ color: theme.colors.textSecondary, minWidth: 60 }}>{cm.secondaryAxisNoun}</span>
                    {(t.secondaryAxis ?? []).length > 0
                      ? <SecondaryAxisPills codes={t.secondaryAxis ?? []} maxVisible={SECONDARY_AXIS_MAX_VISIBLE.dashboard} variant="row" />
                      : <span style={{ color: theme.colors.navy }}>{'—'}</span>}
                  </div>
                </div>
              );
            })()}
          </BentoCard>

          {/* ─── Details (always visible) ────────────────────── */}
          <BentoCard
            title="Details"
            style={{ marginBottom: theme.spacing.lg, minHeight: 260 }}
            contentStyle={{ height: 220, overflow: 'hidden' }}
          >
            {!selectedUuid ? (
              <div style={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                fontSize: 13, color: theme.colors.textSecondary,
              }}>
                Ticket auswählen um Details anzuzeigen
              </div>
            ) : detailLoading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13, textAlign: 'center' }}>
                Lade Details...
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setSelectedUuid(null)} style={{
                    position: 'absolute', top: 0, right: 0,
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: theme.colors.textSecondary,
                  }}>
                    &times;
                  </button>
                  <div style={{
                    fontSize: 14, fontWeight: 600, color: theme.colors.navy,
                    marginBottom: theme.spacing.sm, paddingRight: 24,
                  }}>
                    {selectedTicket?.subject || '(Kein Betreff)'}
                  </div>
                </div>
                {selectedTicket?.emailBodyPreview ? (
                  selectedTicket.emailBodyPreview.includes('<html') || selectedTicket.emailBodyPreview.includes('<div') || selectedTicket.emailBodyPreview.includes('<p') ? (
                    <div
                      onClick={onDetailBodyClick}
                      style={{
                        flex: 1, fontSize: 13, color: theme.colors.navyMid, lineHeight: 1.6,
                        overflowY: 'auto', marginBottom: theme.spacing.sm,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: styleRedactionTokens(DOMPurify.sanitize(selectedTicket.emailBodyPreview, {
                          FORBID_TAGS: ['style', 'link', 'meta', 'head', 'title', 'script', 'img'],
                          FORBID_ATTR: ['style', 'class'],
                          ADD_ATTR: ['data-pii-type', 'data-pii-index'],
                        }), detailRevealCtx),
                      }}
                    />
                  ) : (
                    <div
                      onClick={onDetailBodyClick}
                      style={{
                        flex: 1, fontSize: 13, color: theme.colors.navyMid, lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', overflowY: 'auto',
                        marginBottom: theme.spacing.sm,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: styleRedactionTokens(escapeHtmlText(selectedTicket.emailBodyPreview), detailRevealCtx),
                      }}
                    />
                  )
                ) : (
                  <div style={{
                    flex: 1, fontSize: 13, color: theme.colors.textSecondary, lineHeight: 1.6,
                    marginBottom: theme.spacing.sm,
                    display: 'flex', alignItems: 'center',
                  }}>
                    Inhalt wird nach Verarbeitung angezeigt
                  </div>
                )}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ fontSize: 11, color: theme.colors.textSecondary }}>
                    {selectedTicket?.uploadedByName || selectedTicket?.uploadedByEmail || '—'}
                    {' · '}
                    {selectedTicket ? formatAbsoluteDate(selectedTicket.createdAt) : '—'}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {selectedTicket?.primaryAxis ? (
                      <CategoryBadge category={selectedTicket.primaryAxis} style={{ fontSize: 11, padding: '2px 8px' }} />
                    ) : (
                      <span style={{ color: theme.colors.textSecondary, fontSize: 12 }}>&mdash;</span>
                    )}
                    {selectedTicket?.secondaryAxis && (
                      <SecondaryAxisPills codes={selectedTicket.secondaryAxis} maxVisible={SECONDARY_AXIS_MAX_VISIBLE.dashboard} variant="row" />
                    )}
                    {(selectedTicket?.eskalationsstufe ?? 0) >= 1 && (() => {
                      const n = Math.min(Math.max(selectedTicket!.eskalationsstufe!, 1), 3) as 1 | 2 | 3;
                      const e = theme.colors.eskalation[`s${n}` as 's1' | 's2' | 's3'];
                      return <span style={{ color: e.fg, background: e.bg, border: `1px solid ${e.border}`, borderRadius: 10, fontSize: 10, fontWeight: 600, padding: '1px 8px' }}>Stufe {n}</span>;
                    })()}
                  </span>
                </div>
              </div>
            )}
          </BentoCard>
        </div>

        {/* ════ RIGHT COLUMN ══════════════════════════════ */}
        <div style={{ width: isCompact ? '100%' : 340, flexShrink: 0 }}>

          {/* ─── Schnell-Upload ────────────────────────────── */}
          <BentoCard
            title="Schnell-Upload"
            style={{ marginBottom: theme.spacing.lg }}
          >

            {/* Drop zone — keyboard-operable per WCAG 2.1.1 */}
            <div
              role="button"
              tabIndex={0}
              aria-label="E-Mail-Datei zum Hochladen auswählen oder per Drag & Drop ablegen"
              onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              style={{
                border: `2px dashed ${isDragOver ? theme.colors.navy : theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.md,
                padding: theme.spacing.md, textAlign: 'center',
                fontSize: 13, color: theme.colors.textSecondary, cursor: 'pointer',
                backgroundColor: isDragOver ? theme.colors.bgLight : 'transparent',
                marginBottom: theme.spacing.md,
              }}
            >
              E-Mail hier ablegen
              <input ref={fileInputRef} type="file" accept=".eml,.msg" multiple onChange={handleFileInput} style={{ display: 'none' }} />
            </div>

            {/* Count display — always rendered to pre-allocate height; fades in when uploads present */}
            <div style={{
              fontSize: 12, color: theme.colors.textSecondary, marginBottom: theme.spacing.xs,
              opacity: quickUploads.length > 0 ? 1 : 0,
              transition: 'opacity 0.2s ease',
              minHeight: '1.2em', // reserve stable height even when invisible
            }}>
              {quickUploads.length > 0 ? `${quickUploads.length} hochgeladen` : ' '}
            </div>

            {/* Column headers */}
            <div style={{
              display: 'flex', padding: `0 ${theme.spacing.xs}px ${theme.spacing.xs}px`,
              fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
              textTransform: 'uppercase' as const, letterSpacing: 0.5,
            }}>
              <div style={{ flex: 1 }}>Betreff</div>
              <div style={{ width: 50 }}>Anh.</div>
              <div style={{ width: 100 }} />
            </div>

            {/* Upload rows — all entries, no slice cap */}
            <div className="stx-no-scrollbar" style={{ height: 266, overflowY: 'auto' }}>
              {quickUploads.length === 0 ? (
                <div style={{
                  padding: `${theme.spacing.md}px 0`, textAlign: 'center',
                  fontSize: 13, color: theme.colors.textSecondary,
                }}>
                  Kein Eintrag
                </div>
              ) : (
                quickUploads.map(u => (
                  <div key={u.id} style={{
                    display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
                    padding: `${theme.spacing.sm}px ${theme.spacing.xs}px`,
                  }}>
                    <div style={{
                      flex: 1, fontSize: 13, color: theme.colors.navyMid,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {u.uploading ? u.fileName : (u.subject || u.fileName)}
                    </div>
                    <div style={{ width: 50, fontSize: 12, color: theme.colors.textSecondary, textAlign: 'center' }}>
                      {u.uploading ? '...' : (u.attachmentCount ?? '—')}
                    </div>
                    {u.uploading ? (
                      <div style={{ width: 100, fontSize: 12, color: theme.colors.textSecondary, textAlign: 'right' }}>
                        Hochladen...
                      </div>
                    ) : u.error ? (
                      <div style={{ width: 100, fontSize: 12, color: theme.colors.error, textAlign: 'right' }}>
                        {u.error}
                      </div>
                    ) : (
                      <div style={{ width: 100, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleProcess(u)}
                          disabled={!!u.processing || maintenance}
                          title={maintenance ? 'Wartungsarbeiten — pausiert' : 'Verarbeiten'}
                          style={{
                            padding: '4px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: theme.colors.white, backgroundColor: theme.colors.navy,
                            border: 'none', borderRadius: theme.borderRadius.sm,
                            cursor: u.processing || maintenance ? 'default' : 'pointer',
                            opacity: u.processing || maintenance ? 0.6 : 1,
                          }}
                        >
                          <LightningIcon size={14} color={theme.colors.white} />
                        </button>
                        <button
                          onClick={() => handleDelete(u)}
                          disabled={!!u.deleting}
                          title="Löschen"
                          style={{
                            padding: '3px 6px', fontSize: 13,
                            background: 'none', border: `1px solid ${theme.colors.borderLight}`,
                            borderRadius: theme.borderRadius.sm,
                            cursor: u.deleting ? 'default' : 'pointer',
                            color: theme.colors.textSecondary,
                            opacity: u.deleting ? 0.4 : 1,
                          }}
                        >
                          <TrashIcon size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </BentoCard>

          {/* ─── Auf Überprüfung wartend ──────────────────── */}
          <BentoCard
            title="Auf Überprüfung wartend"
          >

            {/* Column headers */}
            <div style={{
              display: 'flex', padding: `0 ${theme.spacing.xs}px ${theme.spacing.xs}px`,
              fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
              textTransform: 'uppercase' as const, letterSpacing: 0.5,
            }}>
              <div style={{ flex: 1 }}>Betreff</div>
              <div style={{ width: 50 }}>Kat.</div>
              <div style={{ width: 36 }} />
            </div>

            {/* Fixed height (not flex-fill): the left column is composed of fixed-height cards, so a
                bounded scroll list aligns the card bottom with the Details card without growing
                unbounded over the row. ~410 ≈ Details-card bottom on the standard layout. */}
            <div className="stx-no-scrollbar" style={{ height: 410, overflowY: 'auto' }}>
              {pendingHook.loading ? (
                <div style={{
                  padding: `${theme.spacing.md}px 0`, textAlign: 'center',
                  fontSize: 13, color: theme.colors.textSecondary,
                }}>
                  Lade...
                </div>
              ) : pendingHook.rows.length === 0 ? (
                <div style={{
                  padding: `${theme.spacing.md}px 0`, textAlign: 'center',
                  fontSize: 13, color: theme.colors.textSecondary,
                }}>
                  Kein Eintrag
                </div>
              ) : (
                pendingHook.rows.map(t => (
                  <div key={t.uuid} style={{
                    display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
                    padding: `${theme.spacing.sm}px ${theme.spacing.xs}px`,
                  }}>
                    <div style={{
                      flex: 1, minWidth: 0, fontSize: 13, color: theme.colors.navyMid,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {t.subject || '(Kein Betreff)'}
                    </div>
                    {t.primaryAxis ? (
                      <CategoryBadge category={t.primaryAxis} style={{ flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 40 }} />
                    )}
                    <button
                      onClick={() => onNavigate('responses', { ticket: t.uuid })}
                      style={{
                        width: 36, padding: '3px 0', fontSize: 12, fontWeight: 600,
                        fontFamily: theme.font.family,
                        color: theme.colors.navy, backgroundColor: theme.colors.bgLight,
                        border: `1px solid ${theme.colors.borderLight}`,
                        borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      Go
                    </button>
                  </div>
                ))
              )}
              {/* Sentinel for pending card infinite scroll */}
              {!pendingHook.loading && pendingHook.rows.length > 0 && (
                <div ref={pendingHook.sentinelRef} aria-hidden="true" style={{ height: 1 }} />
              )}
              {pendingHook.loadingMore && (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: theme.colors.textSecondary }}>
                  Lade weitere…
                </div>
              )}
            </div>
          </BentoCard>
        </div>
      </div>
    </div>
  );
};

export default DashboardSeite;
